import React, { useEffect, useState, Suspense } from "react";
import { showError, showSuccess } from "../../utils/toastUtils";
import addressAPI from "../../api/addressApi";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import useNumericInput from "../../hooks/useNumericInput";
import {  Row, Col, Form, Button } from "react-bootstrap";
import { MapPin, User, Home, Briefcase, Compass, Trash2, Plus } from "lucide-react";

function Addresses() {
  useAlphabeticInput();
  useNumericInput();

  const [isOpen, setIsOpen] = useState(false);
  const toggleAccordion = () => setIsOpen(!isOpen);
  const [addresses, setAddresses] = useState([]);
  
  // 🌟 THE CRITICAL FIX: Defined the missing loading state track variables hook line
  const [loading, setLoading] = useState(true);

  const INITAL_FORM_DATA = {
    firstName: "",
    lastName: "",
    addressType: "home",
    addressLine1: "",
    addressLine2: "",
    pinCode: "",
    district: "",
    state: "",
    country: "",
  };

  const [formData, setFormData] = useState(INITAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    fetchAddress();
  }, []);

  const fetchAddress = async () => {
    try {
      setLoading(true); // Explicit track execution trigger on
      const res = await addressAPI.getAllAddress();
      if (res.code === 200) {
        setAddresses(res.data || []);
      }
    } catch (err) {
      console.error("Address fetch crash:", err);
    } finally {
      setLoading(false); // Shuts down loading once api lifecycle tasks are completed
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = "Please Enter First Name";
    }

    if (!formData.addressType) {
      newErrors.addressType = "Please Enter Address Type";
    }

    if (!formData.addressLine1) {
      newErrors.addressLine1 = "Please Enter Address";
    }

    if (!formData.pinCode) {
      newErrors.pinCode = "Please Enter PinCode";
    } else if (formData.pinCode.length !== 6) {
      newErrors.pinCode = "PinCode must be exactly 6 digits";
    }

    if (!formData.district) {
      newErrors.district = "Please Enter District";
    }

    if (!formData.state) {
      newErrors.state = "Please Enter State";
    }

    if (!formData.country) {
      newErrors.country = "Please Enter Country";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;
    
    try {
      const res = await addressAPI.addNewAddress(formData);
      if (res.code === 200) {
        showSuccess(res.message || "Address saved to notebook");

        setTimeout(function () {
          setIsOpen(false);
          fetchAddress();
        }, 800);

        setFormData(INITAL_FORM_DATA);
        setErrors({});
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const deleteAddress = async (id) => {
    try {
      const res = await addressAPI.deleteAddress(id);
      if (res.code === 200) {
        showSuccess("Address removed successfully");
        fetchAddress();
      }
    } catch (err) {
      showError(err.message);
    }
  };

  return (
    <>
      <div className="orangeapes-account-dashboard-wrapper mb-5">
        <div className="orangeapes-addresses-container">
          
          {/* HEADER SECTOR */}
          <div className="account-section-header d-flex justify-content-between align-items-center mb-5">
            <div>
              <h5 className="orangeapes-dashboard-main-title mb-2">Notebook Directory</h5>
              <p className="orangeapes-dashboard-subtitle">
                Manage your saved delivery destinations maps records ({addresses.length}).
              </p>
            </div>
            
            <button 
              className={`orangeapes-add-address-trigger-btn d-flex align-items-center gap-2 ${isOpen ? "btn-active" : ""}`}
              onClick={toggleAccordion}
            >
              <Plus size={16} />
              <span>{isOpen ? "Close Panel" : "Add New Address"}</span>
            </button>
          </div>

          {/* DYNAMIC EXPANDABLE ADD ADDRESS ACCORDION PANEL */}
          <div className={`orangeapes-accordion-panel-holder ${isOpen ? "panel-expanded" : ""}`}>
            {isOpen && (
              <div className="orangeapes-accordion-inner-card p-4 p-md-5 mb-5">
                <Form onSubmit={handleSubmit} className="orangeapes-dashboard-form">
                  
                  <div className="form-sub-header-label mb-4">Contact Person Details</div>
                  
                  <Row className="g-4 mb-4">
                    <Col md={6}>
                      <Form.Group className="orangeapes-input-group-row" controlId="firstName">
                        <Form.Label className="orangeapes-form-custom-label">First Name *</Form.Label>
                        <div className="orangeapes-field-input-wrapper">
                          <User size={16} className="orangeapes-field-icon" />
                          <Form.Control
                            type="text"
                            name="firstName"
                            placeholder="First Name"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="orangeapes-blank-input only-alphabets"
                          />
                        </div>
                        {errors.firstName && <span className="orangeapes-error-validation-msg">{errors.firstName}</span>}
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="orangeapes-input-group-row" controlId="lastName">
                        <Form.Label className="orangeapes-form-custom-label">Last Name</Form.Label>
                        <div className="orangeapes-field-input-wrapper">
                          <User size={16} className="orangeapes-field-icon" />
                          <Form.Control
                            type="text"
                            name="lastName"
                            placeholder="Last Name"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="orangeapes-blank-input only-alphabets"
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="form-sub-header-label mb-4 pt-2">Address Specifications</div>

                  {/* LUXURY RE-ENGINEERED ADDRESS TYPE CHIPS SELECTOR */}
                  <div className="orangeapes-address-type-chips-row mb-4">
                    <span className="orangeapes-form-custom-label d-block mb-3">Address Designation Type *</span>
                    <div className="d-flex flex-wrap gap-3">
                      
                      <label className={`orangeapes-premium-radio-chip-card ${formData.addressType === "home" ? "chip-checked" : ""}`}>
                        <input 
                          type="radio" name="addressType" value="home" 
                          checked={formData.addressType === "home"} onChange={handleChange} className="d-none" 
                        />
                        <Home size={14} />
                        <span>Home Residence</span>
                      </label>

                      <label className={`orangeapes-premium-radio-chip-card ${formData.addressType === "work" ? "chip-checked" : ""}`}>
                        <input 
                          type="radio" name="addressType" value="work" 
                          checked={formData.addressType === "work"} onChange={handleChange} className="d-none" 
                        />
                        <Briefcase size={14} />
                        <span>Work / Office</span>
                      </label>

                      <label className={`orangeapes-premium-radio-chip-card ${formData.addressType === "other" ? "chip-checked" : ""}`}>
                        <input 
                          type="radio" name="addressType" value="other" 
                          checked={formData.addressType === "other"} onChange={handleChange} className="d-none" 
                        />
                        <Compass size={14} />
                        <span>Other Destination</span>
                      </label>

                    </div>
                    {errors.addressType && <span className="orangeapes-error-validation-msg d-block mt-2">{errors.addressType}</span>}
                  </div>

                  <Form.Group className="orangeapes-input-group-row mb-4" controlId="addressLine1">
                    <Form.Label className="orangeapes-form-custom-label">Flat / House No. / Building / Street *</Form.Label>
                    <div className="orangeapes-field-input-wrapper">
                      <MapPin size={16} className="orangeapes-field-icon" />
                      <Form.Control
                        type="text"
                        name="addressLine1"
                        placeholder="Street Address details"
                        value={formData.addressLine1}
                        onChange={handleChange}
                        className="orangeapes-blank-input"
                      />
                    </div>
                    {errors.addressLine1 && <span className="orangeapes-error-validation-msg">{errors.addressLine1}</span>}
                  </Form.Group>

                  <Form.Group className="orangeapes-input-group-row mb-4" controlId="addressLine2">
                    <Form.Label className="orangeapes-form-custom-label">Colony / Landmark / Area (Optional)</Form.Label>
                    <div className="orangeapes-field-input-wrapper">
                      <MapPin size={16} className="orangeapes-field-icon" />
                      <Form.Control
                        type="text"
                        name="addressLine2"
                        placeholder="Apartment, suite, unit, reference landmarks etc."
                        value={formData.addressLine2}
                        onChange={handleChange}
                        className="orangeapes-blank-input"
                      />
                    </div>
                  </Form.Group>

                  <Row className="g-4 mb-5">
                    <Col md={4}>
                      <Form.Group className="orangeapes-input-group-row" controlId="pinCode">
                        <Form.Label className="orangeapes-form-custom-label">Postal Pincode *</Form.Label>
                        <div className="orangeapes-field-input-wrapper">
                          <Form.Control
                            type="text"
                            name="pinCode"
                            placeholder="6 Digits Code"
                            maxLength={6}
                            value={formData.pinCode}
                            onChange={handleChange}
                            className="orangeapes-blank-input only-numeric"
                          />
                        </div>
                        {errors.pinCode && <span className="orangeapes-error-validation-msg">{errors.pinCode}</span>}
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="orangeapes-input-group-row" controlId="district">
                        <Form.Label className="orangeapes-form-custom-label">City / District *</Form.Label>
                        <div className="orangeapes-field-input-wrapper">
                          <Form.Control
                            type="text"
                            name="district"
                            placeholder="District Location"
                            value={formData.district}
                            onChange={handleChange}
                            className="orangeapes-blank-input only-alphabets"
                          />
                        </div>
                        {errors.district && <span className="orangeapes-error-validation-msg">{errors.district}</span>}
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="orangeapes-input-group-row" controlId="state">
                        <Form.Label className="orangeapes-form-custom-label">State *</Form.Label>
                        <div className="orangeapes-field-input-wrapper">
                          <Form.Control
                            type="text"
                            name="state"
                            placeholder="State Region"
                            value={formData.state}
                            onChange={handleChange}
                            className="orangeapes-blank-input only-alphabets"
                          />
                        </div>
                        {errors.state && <span className="orangeapes-error-validation-msg">{errors.state}</span>}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="orangeapes-input-group-row mb-5" controlId="country">
                    <Form.Label className="orangeapes-form-custom-label">Country Region *</Form.Label>
                    <div className="orangeapes-field-input-wrapper">
                      <Form.Control
                        type="text"
                        name="country"
                        placeholder="Country"
                        value={formData.country}
                        onChange={handleChange}
                        className="orangeapes-blank-input only-alphabets"
                      />
                    </div>
                    {errors.country && <span className="orangeapes-error-validation-msg">{errors.country}</span>}
                  </Form.Group>

                  <div className="d-flex justify-content-start">
                    <Button className="orangeapes-dashboard-submit-btn py-3 px-5" type="submit">
                      SAVE DESTINATION
                    </Button>
                  </div>

                </Form>
              </div>
            )}
          </div>

          {/* SAVED ADDRESSES GRID BLOCKS DISPLAY */}
          {addresses && addresses.length > 0 ? (
            <Row className="g-4 orangeapes-saved-addresses-grid">
              <Suspense fallback={<div>Aligning active notebook coordinates...</div>}>
                {addresses.map((address) => (
                  <Col md={6} key={address._id}>
                    <div className="orangeapes-address-notebook-card p-4 d-flex flex-column justify-content-between">
                      
                      <div className="card-top-identity-row d-flex justify-content-between align-items-center mb-3">
                        <span className="address-badge-type-pill text-uppercase">{address.addressType || "Home"}</span>
                        <button 
                          className="orangeapes-delete-address-trash-trigger-btn"
                          onClick={() => deleteAddress(address._id)}
                          title="Remove Address"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="card-content-address-data">
                        <h6 className="recipient-fullname-text mb-2">
                          {address.firstName} {address.lastName}
                        </h6>
                        <p className="full-address-paragraphs mb-0">
                          {address.addressLine1}
                          {address.addressLine2 ? `, ${address.addressLine2}` : ""}
                          <br />
                          {address.district}, {address.state} — <strong>{address.pinCode}</strong>
                          <br />
                          <span className="country-region-string text-muted">{address.country}</span>
                        </p>
                      </div>

                    </div>
                  </Col>
                ))}
              </Suspense>
            </Row>
          ) : !loading && (
            <div className="orangeapes-empty-notebook-card text-center py-5">
              <MapPin size={32} className="text-muted mb-3" />
              <h6>No Delivery Coordinates Found</h6>
              <p className="text-muted small mb-0">Your address handbook is empty. Expand the form above to add your first drop destination.</p>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

export default Addresses;